import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  Button,
  Form,
  Input,
  InputNumber,
  notification,
  Popconfirm,
  Space,
} from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { indexBy } from 'utils/utils2';
import { PlusOutlined, PlusSquareOutlined } from '@ant-design/icons';
import DataGrid, { DataGridColDef } from 'components/DataGrid/DataGrid';
import DeleteButton from 'components/DeleteButton';
import { useFormList } from 'hooks/useFormList';
import DF from 'DisplayFramework';
import ButtonOperation from 'components/ButtonOperation';
import SaveButton from 'components/SaveButtonAction';
import {
  addParassetfvvalue,
  deleteParassetfvvalue,
  getParassetfvvalueByContextid,
  ParassetfvvalueRequest,
  ParassetfvvalueResponse,
  updateParassetfvvalue,
} from 'services/assetconfig';
import ModalControllerNV from 'components/ModalControllerNV';
import ParAssetFVDegradationListModal from './ParAssetFVDegradationListModal';
import ParListSelect from 'components/ParListSelect';
import InputRate from 'components/InputRate';
import { isNil } from 'lodash';
import { AuthContext } from 'components/Auth/Auth';
import { setValueByNamePath } from 'utils/utils2';

const NEW_ACTION_ID = -1;
export type ParAssetFutureValueListProps = {
  parcontextid: number;
  refetch: () => void;
};
const ParAssetFutureValueList = (props: ParAssetFutureValueListProps) => {
  const [form] = useForm();
  const { isInspect } = useContext(AuthContext);
  const fieldName = 'ParAssetFutureValue';
  const [method, setMethod] = useState('');
  const { add, remove } = useFormList(form, fieldName, {
    idKey: 'fvid',
    addAsFirst: true,
  });

  const { data: asset, refetch } = useQuery(
    ['getParassetfvvalueByContextid', { parcontextid: props.parcontextid }],
    () =>
      getParassetfvvalueByContextid({
        parcontextid: Number(props.parcontextid),
      }).then((res) => {
        return res.list || [];
      }),
    {
      enabled: !isNil(props.parcontextid) && !isNaN(Number(props.parcontextid)),
    }
  );
  useEffect(() => {
    if (asset) {
      form.setFieldsValue({
        [fieldName]: asset,
      });
    }
  }, [form, asset]);
  const getName = useCallback(
    (name: string) => (record: ParassetfvvalueResponse) => {
      const actions = form.getFieldValue(fieldName);
      const index = indexBy(actions, 'fvid')[record.fvid];
      return [fieldName, index, name];
    },
    [form]
  );
  const onSave = useCallback(
    (record: ParassetfvvalueResponse) => {
      const condition: ParassetfvvalueResponse[] =
        form.getFieldValue(fieldName);
      const index = indexBy(condition, 'fvid')[record.fvid];
      const currentValue = {
        ...condition[index],
      };
      const { fvid } = currentValue;
      if (record.fvevaluationmode==="") {
        return notification.open({
          message: 'Evaluation mode is mandatory',
        });
      } else if (record.fvstartmethod==="") {
        return notification.open({
          message: 'Start Method is mandatory',
        });
      } else {
       (fvid === NEW_ACTION_ID)? 
        addParassetfvvalue({
          request: { ...currentValue, fvid: null as any },
        }).then((response) => {
          if (response.code === '200') {
            notification.success({ message: response.message });
          }
          refetch();
        })
        :  updateParassetfvvalue({
          fvid: Number(fvid),
          request: currentValue,
        }).then((response) => {
          if (response.code === '200') {
            notification.success({
              message: response.message,
            });
            refetch();
          } else {
            notification.error({
              message: response.message,
            });
          }
        });
      }
    },
    [form, refetch]
  );

  const columns = useMemo<DataGridColDef<ParassetfvvalueResponse>[]>(
    () => [
      {
        dataIndex: 'fvid',
        render: (value, record, index) => {
          return record?.fvid === NEW_ACTION_ID ? (
            <Form.Item
              noStyle
              name={getName('fvid')(record)}
              rules={[{ required: true }]}
            >
              <Input disabled />
            </Form.Item>
          ) : (
            <Form.Item>{value}</Form.Item>
          );
        },
        width: '60px',
        fixed: true,
      },
      {
        dataIndex: 'monthsmin',
        render: (value, record) => (
          <Form.Item
            noStyle
            name={getName('monthsmin')(record)}
            rules={[{ required: true }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        ),
        width: '110px',
      },
      {
        dataIndex: 'monthsmax',

        render: (value, record) => (
          <Form.Item
            noStyle
            name={getName('monthsmax')(record)}
            rules={[{ required: true }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        ),
        width: '110px',
      },
   
      {
        dataIndex: 'fvevaluationmode',

        render: (value, record) => (
          <Form.Item
            noStyle
            name={getName('fvevaluationmode')(record)}
            rules={[{ required: true }]}
          >
            <ParListSelect
              parcode="RVEVALMODE"
              onSelect={(e) => {
                setMethod(e);
                setValueByNamePath(form, getName('fvfixedamount')(record), null);
                setValueByNamePath(form, getName('fvpercentage')(record), null);
              }}
            />
          </Form.Item>
        ),
        width: '110px',
      },
      {
        dataIndex: 'fvfixedamount',

        render: (value, record) => (
          <Form.Item
            noStyle
            name={getName('fvfixedamount')(record)}
            rules={[{ required: true }]}
          >
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              disabled={method === 'PERC'}
            />
          </Form.Item>
        ),
        width: '110px',
      },
      {
        dataIndex: 'fvpercentage',

        render: (value, record) => (
          <Form.Item
            noStyle
            name={getName('fvpercentage')(record)}
            rules={[{ required: true }]}
          >
            <InputRate
              style={{ width: '100%' }}
              disabled={method === 'FIXED'}
            />
          </Form.Item>
        ),
        width: '110px',
      },
      {
        dataIndex: 'monthdecrease',

        render: (value, record) => (
          <Form.Item
            noStyle
            name={getName('monthdecrease')(record)}
            rules={[{ required: true }]}
          >
            <InputRate style={{ width: '100%' }} />
          </Form.Item>
        ),
        width: '110px',
      },
      {
        dataIndex: 'fvstartmethod',
        render: (value, record) => (
          <Form.Item
            noStyle
            name={getName('fvstartmethod')(record)}
            rules={[{ required: true }]}
          >
            <ParListSelect parcode="RVSTARTM" />
          </Form.Item>
        ),
        width: '110px',
      },

      {
        dataIndex: 'degradation',
        render: (value, record) => (
          <ModalControllerNV
            modal={({ setVisible, visible }) => (
              <ParAssetFVDegradationListModal
                onCancel={() => {
                  setVisible(false);
                }}
                visible={visible}
                fvid={record.fvid}
                parcontextid={record.parcontextid}
              />
            )}
            content={({ setVisible, visible }) => (
              <Button
                size="small"
                onClick={() => {
                  setVisible(true);
                }}
                style={{ border: '1px solid primary' }}
                type="default"
                icon={<PlusSquareOutlined style={{ color: 'primary' }} />}
                htmlType="button"
                disabled={record?.fvid === -1}
              >
                {'degradation List '}
              </Button>
            )}
          />
        ),

        width: '110px',
      },

      {
        dataIndex: 'Actions',
        render: (_, record, index) =>
          record?.fvid === NEW_ACTION_ID ? (
            <Space>
              <SaveButton
                comcode="save"
                size="small"
                onClick={() => onSave(record)}
                htmlType="button"
              />

              <Popconfirm
                title="Are you sure to delete this value?"
                onConfirm={() => {
                  remove(record.fvid);
                }}
                okText="Yes"
                cancelText="No"
              >
                <DeleteButton onlyIcon custom={{height:35}}/>
              </Popconfirm>
            </Space>
          ) : (
            <Space>
              <SaveButton
                comcode="update"
                size="small"
                onClick={() => !isInspect&&onSave(record)}
                htmlType="button"
              />
              <Popconfirm
                title="Are you sure to delete this Condition?"
                onConfirm={() => {
                  deleteParassetfvvalue({
                    fvid: record.fvid,
                  }).then((res) => {
                    notification.success({ message: res.message });
                    refetch();
                  });
                }}
              >
                <DeleteButton onlyIcon custom={{height:35}}/>
              </Popconfirm>
            </Space>
          ),
        width: '100px',
        fixed: 'right',
      },
    ],

    [getName, onSave, refetch, remove, method,isInspect]
  );
  const addRow = () => {
    const row: ParassetfvvalueRequest = {
      fvid: NEW_ACTION_ID,
      fvevaluationmode: '',
      fvfixedamount: 0,
      fvpercentage: 0,
      fvstartmethod: '',
      monthdecrease: 0,
      monthsmax: 0,
      monthsmin: 0,
      parcontextid: props.parcontextid,
    };
    add(row);
  };
  return (
    <DF.Provider pagcode="parassetfvcontextdetail">
      <Form form={form}>
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => {
            const assets: ParassetfvvalueResponse[] = getFieldValue(fieldName);
            const isNewItemInTable =
              asset?.find(({ fvid }) => fvid === NEW_ACTION_ID) !== undefined;
            return (
              <DataGrid
                titleComcode="ParAssetFutureValueList"
                rowKey="useid"
                columns={columns}
                dataSource={assets}
                hideFilters
                extraActions={
                  <ButtonOperation
                    comcode="Add"
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => !isInspect&&addRow()}
                    disabled={isNewItemInTable}
                  />
                }
              />
            );
          }}
        </Form.Item>
      </Form>
    </DF.Provider>
  );
};

export default ParAssetFutureValueList;
